import * as React from 'react';
import type { StoryCard } from '@org/model';
import { cn } from '../../../utils';
import type { StoryLabels } from './labels';
import { STORY_CARD_REGISTRY } from './registry';
import { StoriesStyles } from './stories-styles';
import {
  STORIES_ROOT_CLASS,
  STORY_STAGE_HEIGHT,
  STORY_STAGE_WIDTH,
  storyPageTheme,
  storyPosterStyle,
  type StoryPageTheme,
} from './stories-theme';
import type { StoryCardComponentProps, StoryLang, StoryRenderContext, StoryUnit, StoryWoman } from './story-types';

/** Renders a card through the registry (nothing for an unknown type, e.g. hand-edited JSON). */
export function StoryCardBody({ card, ctx }: StoryCardComponentProps) {
  const entry = STORY_CARD_REGISTRY[card.type] as (typeof STORY_CARD_REGISTRY)[StoryCard['type']] | undefined;
  if (!entry) return null;
  const Component = entry.Component as React.ComponentType<StoryCardComponentProps>;
  return <Component card={card} ctx={ctx} />;
}

/** CSS variables + classes of a page's stage. */
export function storyStageProps(theme: StoryPageTheme): { className: string; style: React.CSSProperties } {
  return {
    className: cn('zs-stage zs-grain', theme.dark && 'zs-dark', theme.halftone && 'zs-halftone'),
    style: { '--zs-pbg': theme.bg, '--zs-pfg': theme.fg, '--zs-pacc': theme.acc } as React.CSSProperties,
  };
}

export interface StoryCardViewProps {
  card: StoryCard;
  woman: StoryWoman;
  lang: StoryLang;
  labels: StoryLabels;
  unit?: StoryUnit | null;
  posterStyleIndex?: number;
  /** rendered width in px (the 390×844 page is scaled to it). Default 390. */
  width?: number;
  /** big faint page number in the corner, as in the viewer (card #1 is page 2) */
  pageNumber?: number;
  /** set false when a StoryViewer / another StoryCardView on the page already injected the stylesheet */
  withStyles?: boolean;
  className?: string;
}

/**
 * One card alone, as a still picture inside the scoped story root — for
 * thumbnails. Nothing in it is interactive (videos show their poster).
 */
export function StoryCardView({
  card,
  woman,
  lang,
  labels,
  unit,
  posterStyleIndex,
  width = STORY_STAGE_WIDTH,
  pageNumber,
  withStyles = true,
  className,
}: StoryCardViewProps) {
  const scale = width / STORY_STAGE_WIDTH;
  const theme = storyPageTheme({ kind: 'card', card }, posterStyleIndex);
  const stage = storyStageProps(theme);
  const ctx: StoryRenderContext = {
    woman,
    unit,
    lang,
    labels,
    posterStyle: storyPosterStyle(posterStyleIndex),
    theme,
    interactive: false,
    paused: true,
  };
  return (
    <div
      className={cn(STORIES_ROOT_CLASS, 'zs-thumb', className)}
      style={{ width, height: STORY_STAGE_HEIGHT * scale }}
      data-testid="story-card-view"
    >
      {withStyles ? <StoriesStyles /> : null}
      <div className="zs-scale" style={{ transform: `scale(${scale})` }}>
        <div className={stage.className} style={stage.style}>
          {pageNumber !== undefined && card.type !== 'video' ? (
            <div className="zs-wmk" aria-hidden="true" style={{ color: theme.fg }}>
              {String(pageNumber).padStart(2, '0')}
            </div>
          ) : null}
          <div className="zs-page">
            <StoryCardBody card={card} ctx={ctx} />
          </div>
        </div>
      </div>
    </div>
  );
}
